/**
 * 岗位管理 API
 * 对应 foundation-service (7092)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type {
  PositionInfo,
  PositionQueryParams,
  PositionFormData,
  PositionCategoryOption,
  PositionStatistics,
  UserPositionInfo,
} from './types';

const service = () => unifiedApi.service('foundation');

/** 获取岗位列表 */
export const getPositionList = async (params?: PositionQueryParams) => {
  return handleApiResponse(
    service().get<{ list: PositionInfo[]; total: number }>('/positions', { params })
  );
};

/** 获取岗位详情 */
export const getPositionDetail = async (id: number) => {
  return handleApiResponse(
    service().get<PositionInfo>(`/positions/${id}`)
  );
};

/** 创建岗位 */
export const createPosition = async (data: PositionFormData) => {
  return handleApiResponse(
    service().post<{ id: number }>('/positions', data)
  );
};

/** 更新岗位 */
export const updatePosition = async (id: number, data: PositionFormData) => {
  return handleApiResponse(
    service().put(`/positions/${id}`, data)
  );
};

/** 删除岗位 */
export const deletePosition = async (id: number) => {
  return handleApiResponse(
    service().delete(`/positions/${id}`)
  );
};

/** 批量删除岗位 */
export const batchDeletePositions = async (positionIds: number[]) => {
  return handleApiResponse(
    service().post('/positions/batch-delete', { position_ids: positionIds })
  );
};

/** 获取岗位类别列表 */
export const getPositionCategories = async () => {
  return handleApiResponse(
    service().get<{ categories: PositionCategoryOption[] }>('/positions/categories')
  );
};

/** 获取岗位统计 */
export const getPositionStatistics = async () => {
  return handleApiResponse(
    service().get<PositionStatistics>('/positions/statistics')
  );
};

/** 获取岗位用户列表 */
export const getPositionUsers = async (positionId: number, params?: { page?: number; page_size?: number }) => {
  return handleApiResponse(
    service().get<{ list: any[]; total: number }>(`/positions/${positionId}/users`, { params })
  );
};

/** 分配岗位给用户 */
export const assignPosition = async (data: { user_id: number; position_id: number; is_primary?: boolean }) => {
  return handleApiResponse(
    service().post('/positions/assign', data)
  );
};

/** 批量分配岗位 */
export const batchAssignPosition = async (data: { user_ids: number[]; position_id: number }) => {
  return handleApiResponse(
    service().post('/positions/batch-assign', data)
  );
};

/** 移除用户岗位 */
export const removePosition = async (data: { user_id: number; position_id: number }) => {
  return handleApiResponse(
    service().post('/positions/remove', data)
  );
};

/** 设置主岗位 */
export const setPrimaryPosition = async (data: { user_id: number; position_id: number }) => {
  return handleApiResponse(
    service().post('/positions/set-primary', data)
  );
};

/** 获取用户岗位列表 */
export const getUserPositions = async (userId: number) => {
  return handleApiResponse(
    service().get<{ list: UserPositionInfo[] }>(`/users/${userId}/positions`)
  );
};

/** 岗位管理 API 对象 */
export const positionApi = {
  getList: getPositionList,
  getDetail: getPositionDetail,
  create: createPosition,
  update: updatePosition,
  delete: deletePosition,
  batchDelete: batchDeletePositions,
  getCategories: getPositionCategories,
  getStatistics: getPositionStatistics,
  getUsers: getPositionUsers,
  assign: assignPosition,
  batchAssign: batchAssignPosition,
  remove: removePosition,
  setPrimary: setPrimaryPosition,
  getUserPositions,
};

export default positionApi;
